'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  signInWithPopup,
  signInWithCustomToken,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  onIdTokenChanged,
  type User,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { getFirebaseAuth, getFirebaseDb } from './firebase';
import { logClientError } from './client-error-logger';
import { readBootCache, saveBootProfile, clearBootCache, type BootTenant } from './boot-cache';
import type { GMSUser, OwnerSession } from '@/types';
import { landingPathFor } from '@/lib/landing';

// 1 dòng dropdown WorkspaceSwitcher (Sidebar) — BR đối tác TA (2026-09-14): 1 gmail quản lý nhiều
// tenant (tenant nhà + tenant khách hàng của TA, vd Laki Apartment). >1 phần tử = hiện dropdown.
export interface WorkspaceOption {
  tenantId: string;
  tenantName: string;
  role: string;
}

interface AuthContextType {
  user: GMSUser | null;
  /** Phiên chủ nhà (Owner Portal v2) — song song `user`, KHÔNG BAO GIỜ cả 2 cùng khác null (owner
   *  không có `gmsRole`). `AppShell` đọc field này để render `OwnerShell` thay cây Provider nhân viên. */
  ownerSession: OwnerSession | null;
  firebaseUser: User | null;
  /** Ảnh chụp tenant do sync-profile trả kèm (TenantProvider dùng để khỏi getDoc lần nữa). */
  bootTenant: BootTenant | null;
  /** Danh sách workspace (tenant nhà + workspace cấp thêm) — undefined/1 phần tử = ẩn dropdown. */
  workspaces: WorkspaceOption[] | null;
  /** Đổi workspace đang làm việc: mint claim mới + đăng nhập lại + reload để mọi provider đọc đúng tenant. */
  switchWorkspace: (tenantId: string) => Promise<void>;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  error: string | null;
  setError: (e: string | null) => void;
  /** true = đã tự thử lại `sync-profile` hết số lần cho phép mà vẫn treo/lỗi mạng (KHÔNG phải bị từ
   *  chối quyền — phiên đăng nhập vẫn còn). AppShell hiện màn "Thử lại" tại chỗ thay vì đá về login. */
  bootError: boolean;
  /** Đang trong 1 trong các lần tự thử lại `sync-profile` — AppShell hiện animation + đếm lần. */
  bootRetry: { attempt: number; max: number } | null;
  /** Thử lại `sync-profile` bằng phiên Firebase hiện có (không cần đăng nhập lại). */
  retryBoot: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

type SyncResult = { profile: GMSUser | null; tenant?: BootTenant | null; pendingSale?: boolean; workspaces?: WorkspaceOption[] };

const SYNC_TIMEOUT_MS = 15_000;
// 2 lần tự thử lại (3 lượt gọi tổng) trước khi đẩy lỗi lên cho UI hiện nút "Thử lại" tay — sóng
// 4G/5G chập chờn thường tự ổn định lại sau vài giây, không đáng bắt user tự bấm ngay từ lần đầu.
const AUTO_RETRY_DELAYS_MS = [1_500, 3_000];

// Treo do timeout (AbortSignal) hoặc mất kết nối hẳn ("Failed to fetch") — CÓ THỂ tự khỏi nếu sóng
// ổn định lại, đáng tự thử lại. Lỗi HTTP status (401/500 — server ĐÃ trả lời, chỉ là từ chối) thử lại
// ngay lập tức vô ích — để user tự bấm "Thử lại" (khi đó `getIdToken()` có cơ hội tự refresh token).
function isNetworkStall(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  if (err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')) return true;
  return false;
}

// Hồ sơ GMS được resolve SERVER-SIDE qua /api/auth/sync-profile (Admin SDK).
// Trước đây hàm này đọc/ghi collection `users` TRỰC TIẾP từ browser (migrate-UID, tra theo email)
// → đòi quyền ghi `users` từ client, mâu thuẫn với Security Rules chặt (chống tự nâng gmsRole/tenantId).
// Nay client chỉ gửi idToken; server làm toàn bộ và trả profile.
//
// `providerPhotoURL`/`providerDisplayName` (2026-09-14): Firebase Auth CHỈ chép ảnh/tên Google vào
// field chính của user LÚC TẠO tài khoản — đăng nhập lại sau đó (đổi ảnh đại diện Google) KHÔNG tự
// đồng bộ (hành vi mặc định của Firebase). Ảnh/tên MỚI NHẤT chỉ có ở `fbUser.providerData[]` (client),
// ID token claims cũng cũ y hệt field chính → gửi kèm để server tự self-heal Firestore mỗi lần login,
// khỏi kẹt avatar cũ vĩnh viễn (bug thật: gms.support@ledifa.com đổi avatar không lên GMS).
async function syncUserProfileOnce(fbUser: User): Promise<SyncResult> {
  const idToken = await fbUser.getIdToken();
  const google = fbUser.providerData.find(p => p.providerId === 'google.com');
  const res = await fetch('/api/auth/sync-profile', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, providerPhotoURL: google?.photoURL, providerDisplayName: google?.displayName }),
    signal: AbortSignal.timeout(SYNC_TIMEOUT_MS),
  });
  if (!res.ok) throw new Error(`sync-profile failed: ${res.status}`);
  return (await res.json()) as SyncResult;
}

// Sóng yếu (5G/4G chập chờn) khiến fetch TREO thay vì báo lỗi ngay — không có timeout thì màn "Đang
// xác thực tài khoản…" đứng im vô thời hạn (bug thật 2026-09-21, NV chờ >3 phút trên 5G yếu ở HHT,
// về nhà có wifi F5 thì /timeline lên trong 3-5s — đúng bằng thời gian chuỗi boot bình thường, chứng
// tỏ vấn đề là mạng TREO request chứ không phải logic chậm). `onAttempt` báo tiến độ cho UI hiện
// animation "đang thử lại", tránh màn hình đứng im trông như app đã chết.
async function syncUserProfile(fbUser: User, onAttempt?: (attempt: number, max: number) => void): Promise<SyncResult> {
  const maxAttempts = AUTO_RETRY_DELAYS_MS.length + 1;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await syncUserProfileOnce(fbUser);
    } catch (err) {
      if (attempt === maxAttempts || !isNetworkStall(err)) throw err;
      onAttempt?.(attempt + 1, maxAttempts);
      await new Promise(r => setTimeout(r, AUTO_RETRY_DELAYS_MS[attempt - 1]));
    }
  }
  throw new Error('unreachable'); // vòng lặp trên luôn return hoặc throw trước khi tới đây
}

// Claim cache trên thiết bị có thể được cấp TRƯỚC khi custom claim tenantId/gmsRole được set (đăng
// nhập từ lâu / đổi claim / deploy rules mới). So khớp claim với profile (nguồn chân lý từ users doc);
// lệch/thiếu → force refresh token 1 lần để nạp claim mới nhất từ backend (tự lành, khỏi re-login tay).
// Dùng chung cho CẢ luồng đăng nhập lẫn `retryBoot()` — tách riêng để 2 nơi không lệch logic.
async function refreshClaimsIfStale(fbUser: User, profile: GMSUser): Promise<void> {
  try {
    const tok = await fbUser.getIdTokenResult();
    const claimTid = tok.claims.tenantId as string | undefined;
    const claimRole = tok.claims.gmsRole as string | undefined;
    // ⚠️ Đang impersonate (xem app/api/admin/tenants/[id]/impersonate) → claim.tenantId CỐ Ý khác
    // users/{uid}.tenantId thật (operator vẫn là gardenia) — ĐỪNG force-refresh (sẽ nạp lại claim
    // THẬT, tự thoát impersonate giữa phiên mà không ai biết).
    if (!tok.claims.impersonating && ((profile.tenantId && claimTid !== profile.tenantId) || !claimRole)) {
      await fbUser.getIdToken(true);
    }
  } catch (err) {
    logClientError({ route: 'lib/auth.tsx', action: 'force_refresh_token_claims', message: err instanceof Error ? err.message : String(err) });
    /* refresh best-effort — không chặn đăng nhập */
  }
}

export function AuthProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const router = useRouter();
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [user, setUser] = useState<GMSUser | null>(null);
  const [ownerSession, setOwnerSession] = useState<OwnerSession | null>(null);
  const [bootTenant, setBootTenant] = useState<BootTenant | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceOption[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bootError, setBootError] = useState(false);
  const [bootRetry, setBootRetry] = useState<{ attempt: number; max: number } | null>(null);
  // Theo dõi phiên đã đồng bộ lần đầu (onAuthStateChanged) + claim gần nhất đã biết — dùng để
  // listener onIdTokenChanged bên dưới phân biệt "vừa đăng nhập" (đã xử lý ở effect này, bỏ qua) với
  // "token vừa tự refresh cho phiên ĐANG mở" (cần tái kiểm claim). Ref, không phải state — không cần
  // re-render khi đổi.
  const syncedUidRef = useRef<string | null>(null);
  const lastClaimRef = useRef<{ tid?: string; role?: string } | null>(null);
  // 🔴 Bug thật 2026-10-26 (nanghome, `gms.support@ledifa.com`, 6 lỗi permission-denied cùng giây lúc
  // `lastSignInTime` khớp thời điểm switchWorkspace): `signInWithCustomToken()` trong `switchWorkspace()`
  // bắn CẢ 2 listener `onAuthStateChanged`/`onIdTokenChanged` bên dưới NGAY LẬP TỨC — mỗi listener tự
  // làm NGUYÊN 1 lượt syncUserProfile+áp state ĐỘC LẬP, chạy SONG SONG với chính vòng retry-xác-nhận
  // (8×400ms) + sync/áp state CỦA switchWorkspace(). Ai xong trước set `user.tenantId` TRƯỚC — nếu là
  // 1 trong 2 listener (không đợi gì cả) thì building-context/todo-counts/… mount NGAY với tenantId mới
  // trong khi kênh realtime Firestore SDK CÓ THỂ chưa kịp swap token xong (đó chính là lý do vòng retry
  // tồn tại) → permission-denied race, y hệt cơ chế `window.location.href` cũ đã bỏ 2026-10-19 nhưng
  // dưới dạng KHÁC (đua với listener trong-tab thay vì đua với IndexedDB qua reload). Cờ này bật NGAY
  // trước `signInWithCustomToken()` trong switchWorkspace(), tắt lại ở `finally` — 2 listener thấy bật
  // thì NHƯỜNG HẲN cho chính switchWorkspace() làm (đã có vòng retry đúng), không tự sync+áp trùng.
  const switchingWorkspaceRef = useRef(false);

  // Áp kết quả sync-profile vào state — dùng chung cho luồng đăng nhập (onAuthStateChanged) lẫn
  // `retryBoot()` để 2 nơi không lệch logic khi sửa sau này.
  const applySyncResult = useCallback(async (fbUser: User, result: SyncResult) => {
    const { profile, tenant, pendingSale, workspaces: ws } = result;
    if (profile) {
      await refreshClaimsIfStale(fbUser, profile);
      setUser(profile);
      setBootTenant(tenant ?? null);
      setWorkspaces(ws && ws.length > 1 ? ws : null);
      saveBootProfile(fbUser.uid, profile, tenant ?? null);
      syncedUidRef.current = fbUser.uid;
      lastClaimRef.current = { tid: profile.tenantId, role: profile.role };
    } else {
      clearBootCache();
      await firebaseSignOut(getFirebaseAuth());
      setUser(null);
      setBootTenant(null);
      setWorkspaces(null);
      setError(pendingSale
        ? 'Yêu cầu truy cập của bạn đã được gửi — vui lòng chờ chủ nhà (tenant) duyệt.'
        : 'Tài khoản chưa được cấp quyền truy cập GMS. Liên hệ quản trị viên.');
    }
  }, []);

  // Thử lại `sync-profile` bằng phiên Firebase hiện có — dùng khi `bootError` (mạng treo/lỗi sau khi
  // đã tự thử lại hết số lần cho phép), KHÔNG cần đăng nhập lại vì phiên Firebase vẫn còn nguyên.
  const retryBoot = useCallback(async () => {
    const fbUser = getFirebaseAuth().currentUser;
    if (!fbUser) { setBootError(false); return; } // phiên đã mất hẳn — để onAuthStateChanged tự xử lý
    setBootError(false);
    setLoading(true);
    try {
      const result = await syncUserProfile(fbUser, (attempt, max) => setBootRetry({ attempt, max }));
      setBootRetry(null);
      await applySyncResult(fbUser, result);
    } catch (err) {
      setBootRetry(null);
      logClientError({ route: 'lib/auth.tsx', action: 'retry_boot_sync', message: err instanceof Error ? err.message : String(err) });
      setBootError(true);
    } finally {
      setLoading(false);
    }
  }, [applySyncResult]);

  useEffect(() => {
    const auth = getFirebaseAuth();
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      // ⚠️ /hk (housekeeper self-service, app/hk/page.tsx) đăng nhập Firebase bằng CUSTOM TOKEN uid
      // `hk_{hkId}` — dùng CHUNG 1 Firebase App instance với app chính (lib/firebase.ts chỉ init 1
      // app/origin) nên listener này CŨNG bắn khi /hk đăng nhập, dù /hk không nằm trong (app)/ và
      // không dùng useAuth() ở đâu cả. Bug thật 2026-09-14 (8 lớp vá /hk "permission-denied" trước đó
      // đều KHÔNG phải gốc rễ): uid `hk_*` không có doc `users/{uid}` → syncUserProfile() trả
      // `profile:null` → nhánh else bên dưới tự `firebaseSignOut()` — TỰ ĐĂNG XUẤT phiên /hk vừa
      // đăng nhập thành công vài giây trước, khớp CHÍNH XÁC log lỗi cũ (`signInWithCustomToken`
      // thành công thật qua Admin SDK, `currentUser` về `null` ~1 phút sau). Bỏ qua HOÀN TOÀN uid
      // dạng này — không phải lỗi để signOut, không phải phiên GMS để sync.
      if (fbUser?.uid.startsWith('hk_')) { setLoading(false); return; }
      // ⚠️ CÙNG LỚP BUG với /hk ở trên nhưng cho Owner Portal (chủ nhà) — phát hiện 2026-10-11 sau
      // NHIỀU vòng debug sai hướng (webview Zalo, COOP popup, storage-partitioning redirect, CSP —
      // tất cả đều là hiện tượng PHỤ, không phải gốc rễ). Chủ nhà đăng nhập Google (claim CHỈ
      // {tenantId, ownerRole:'owner'}, KHÔNG có `gmsRole`) — listener NÀY vẫn bắn vì DÙNG CHUNG
      // Firebase App instance với mọi phiên khác. `syncUserProfile()` tìm `users/{uid}.gmsRole` →
      // KHÔNG có (chủ nhà không phải nhân viên nội bộ) → trả `profile:null` → nhánh else bên dưới tự
      // `firebaseSignOut()` NGAY SAU KHI vừa đăng nhập thành công vài giây trước. Owner Portal v2
      // (2026-10-11): KHÔNG chỉ bỏ qua như trước — XÂY hẳn 1 phiên `ownerSession` để AppShell render
      // `OwnerShell` (Sidebar rút gọn theo `grants`, dùng chung UI Timeline/Booking với nhân viên
      // nhưng lấy dữ liệu qua `/api/owner/*` — xem docs/owner-portal.md).
      if (fbUser) {
        try {
          // 🔴 LUÔN force-refresh (KHÔNG chỉ khi "phát hiện thiếu" như bản vá trước — case thật
          // 2026-10-11 sau cùng ngày: bản vá "chỉ refresh khi thiếu" vẫn fail vì `onAuthStateChanged`
          // bắn >1 LẦN CHỒNG NHAU cho CÙNG 1 lượt đăng nhập (race condition đã biết trong chính file
          // này — xem các đoạn xử lý `syncedUidRef`/cờ `gms-impersonating` bên dưới, vốn chỉ chống
          // được race cho NHÁNH NHÂN VIÊN, không che được nhánh owner mới thêm). 2 lần bắn chạy
          // SONG SONG (callback async, không có khoá) — lần đọc token CACHE (chưa refresh) rơi vào
          // nhánh `if (tok.claims.ownerRole !== 'owner')` SAI, đi tiếp xuống `syncUserProfile()` (nhân
          // viên) → `profile:null` → `firebaseSignOut()` NGAY LẬP TỨC, xoá mất kết quả của lần CÒN
          // LẠI (đã refresh đúng, gọi `/api/owner/profile` thành công) — Network tab THẬT cho thấy cả
          // 2 nhánh cùng chạy xen kẽ trong 1 lượt test. Để MỌI lần callback bắn đều nhất quán đi đúng
          // nhánh owner (dù bắn bao nhiêu lần), bắt buộc TOÀN BỘ các lần đều tự force-refresh TRƯỚC
          // khi quyết định, không được dựa vào cache — chấp nhận tốn thêm round-trip cho MỌI lần đăng
          // nhập (staff lẫn owner, hiếm khi xảy ra, không phải hot-path) để đổi lấy tính nhất quán.
          const tok = await fbUser.getIdTokenResult(true);
          if (tok.claims.ownerRole === 'owner') {
            const token = await fbUser.getIdToken();
            const res = await fetch('/api/owner/profile', { headers: { Authorization: `Bearer ${token}` } });
            if (res.ok) {
              const profile = await res.json() as { tenantId: string; tenantName: string; buildings: OwnerSession['buildings'] };
              setOwnerSession({ uid: fbUser.uid, email: fbUser.email ?? '', ...profile });
            } else {
              // Claim owner hợp lệ nhưng /api/owner/profile từ chối (vd ownerAccounts bị TA gỡ sau khi
              // mint claim) — không có gì để hiển thị, cũng KHÔNG phải lỗi để signOut (claim vẫn đúng,
              // chỉ là chưa/không còn được cấp toà nào — để OwnerShell tự báo "liên hệ TA").
              const body = await res.json().catch(() => ({}));
              setOwnerSession({ uid: fbUser.uid, email: fbUser.email ?? '', tenantId: tok.claims.tenantId as string, tenantName: '', buildings: [] });
              if (res.status >= 500) logClientError({ route: 'lib/auth.tsx', action: 'load_owner_profile', message: body.error || `Lỗi ${res.status}` });
            }
            setLoading(false);
            return;
          }
        } catch (err) {
          logClientError({ route: 'lib/auth.tsx', action: 'check_owner_claim', message: err instanceof Error ? err.message : String(err) });
          // Không đọc được claim/profile → an toàn nhất là rơi xuống luồng GMS bình thường bên dưới
          // (sync-profile tự xử lý tiếp, không signOut nhầm chỉ vì 1 lần lỗi tạm thời).
        }
      }
      setOwnerSession(null); // fbUser null (đăng xuất) hoặc không phải owner → đảm bảo không giữ phiên owner cũ
      setFirebaseUser(fbUser);
      if (fbUser && switchingWorkspaceRef.current) return; // switchWorkspace() đang tự lo sync+áp state (đã retry-xác-nhận) — đừng đua
      if (fbUser) {
        // ⚡ Hiện UI NGAY từ cache lần đăng nhập trước (nếu đúng uid) rồi mới đồng bộ ở nền.
        // Không có cache → giữ màn chờ như cũ. Xem lib/boot-cache.ts (KHÔNG phải ranh giới bảo mật).
        //
        // Bug thật 2026-09-14 (race condition multi-workspace): cache khoá theo uid, KHÔNG khoá theo
        // tenant đang hoạt động — tài khoản workspaceSwitch/impersonate đổi qua lại giữa các tenant thì
        // cache có thể còn giữ tenant CŨ từ lần trước. Tin ngay `cached.profile` khiến `user.tenantId`
        // sai trong ~1-3s đầu → mọi hook đọc thẳng `user?.tenantId` (use-todos/use-unread-chats/
        // permissions-context, KHÔNG qua TenantProvider) fire nhầm tenant → PERMISSION_DENIED hàng
        // loạt (33 dòng /reports 2026-09-14). Đối chiếu `cached.profile.tenantId` với claim token HIỆN
        // TẠI (đọc local, không force-refresh nên gần như tức thì) trước khi tin.
        // ⚠️ Cờ `gms-impersonating` (sessionStorage, đặt trong impersonate() TRƯỚC khi reload, xem
        // components/ImpersonationBanner.tsx) sống sót qua reload cứng CÙNG TAB — dùng làm tín hiệu
        // "vừa impersonate, đừng tin token/cache cũ" để ép force-refresh ĐÚNG lúc cần, không tốn
        // thêm round-trip cho mọi lượt đăng nhập bình thường khác. Ép TRƯỚC cả bước đối chiếu cache
        // lẫn bước gọi sync-profile bên dưới (fbUser.getIdToken() không force sẽ tự dùng lại bản vừa
        // force-refresh này, không tốn thêm round-trip lần 2) — xem chi tiết bug ở comment dưới.
        const justImpersonated = (() => { try { return !!sessionStorage.getItem('gms-impersonating'); } catch { return false; } })();
        if (justImpersonated) {
          try {
            await fbUser.getIdToken(true);
          } catch (err) {
            // Best-effort pre-warm — syncUserProfile() bên dưới vẫn tự retry+báo lỗi thật nếu mạng
            // hỏng, nhưng vẫn phải log (không được nuốt im lặng) để biết lượt force-refresh này có
            // thực sự chạy hay không khi soát lại bug tái phát.
            logClientError({ route: 'lib/auth.tsx', action: 'force_refresh_after_impersonate', message: err instanceof Error ? err.message : String(err) });
          }
        }

        const cached = readBootCache(fbUser.uid);
        let cacheConfirmed = !!cached;
        if (cached) {
          try {
            // ⚠️ Đọc LOCAL (không force-refresh) từng là điểm hở của chính cơ chế đối chiếu này: ngay
            // sau signInWithCustomToken() (impersonate) + reload cứng, token local trên trang MỚI có
            // thể chưa kịp đồng bộ persistence IndexedDB từ trang CŨ — `tok.claims.tenantId` đọc được
            // vẫn là claim CŨ, trùng khít với `cached.profile.tenantId` (cũng cũ) nên "xác nhận" nhầm,
            // set `user` theo cache SAI TENANT → use-todos/use-unread-chats (đọc thẳng user?.tenantId,
            // không qua TenantProvider) bắn PERMISSION_DENIED hàng loạt (bug thật tái phát 3 lần
            // 2026-09-21→23, 2 lần vá trước ở impersonate() — force-refresh + đọc thử phía TRANG CŨ —
            // không đủ vì Auth SDK trang MỚI khởi tạo lại từ persistence, không kế thừa state trang cũ).
            // Đã force-refresh ở trên khi `justImpersonated` nên đọc `false` ở đây vẫn lấy đúng bản mới.
            const tok = await fbUser.getIdTokenResult(false);
            const claimTid = tok.claims.tenantId as string | undefined;
            if (claimTid && claimTid !== cached.profile.tenantId) cacheConfirmed = false;
          } catch (err) {
            logClientError({ route: 'lib/auth.tsx', action: 'confirm_boot_cache_claim', message: err instanceof Error ? err.message : String(err) });
            // Không xác nhận được → vẫn tin cache như trước (best-effort, không chặn UI vì lỗi phụ).
          }
        }
        if (cached && cacheConfirmed) {
          setUser(cached.profile);
          setBootTenant(cached.tenant);
          setLoading(false);
        } else {
          setLoading(true);
        }
        try {
          const result = await syncUserProfile(fbUser, (attempt, max) => setBootRetry({ attempt, max }));
          setBootRetry(null);
          setBootError(false);
          await applySyncResult(fbUser, result);
        } catch (err) {
          setBootRetry(null);
          logClientError({ route: 'lib/auth.tsx', action: 'sync_user_profile', message: err instanceof Error ? err.message : String(err) });
          // Đã hiện UI từ cache mà đồng bộ lỗi (mất sóng giữa chừng) → GIỮ nguyên phiên cache,
          // đừng đá user ra màn hình lỗi; lần mở/refocus sau sẽ đồng bộ lại. Cache KHÔNG được xác
          // nhận khớp claim (nhánh mới ở trên) thì coi như không có cache → đây là lỗi MẠNG (đã tự
          // thử lại hết `AUTO_RETRY_DELAYS_MS`, KHÔNG phải bị từ chối quyền) — bật `bootError` để
          // AppShell hiện màn "Thử lại" tại chỗ, KHÔNG đá về màn đăng nhập (phiên Firebase vẫn còn).
          if (!cached || !cacheConfirmed) {
            setUser(null);
            setBootError(true);
          }
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
        setBootTenant(null);
        setWorkspaces(null);
        setLoading(false);
        setBootError(false);
        setBootRetry(null);
        syncedUidRef.current = null;
        lastClaimRef.current = null;
      }
    });
    return unsub;
    // `applySyncResult` cố ý KHÔNG nằm trong deps: nó là `useCallback` deps `[]` (chỉ dùng setter
    // useState, ổn định suốt đời component) nên tham chiếu không bao giờ đổi — thêm vào đây không
    // đổi hành vi, chỉ đổi cảnh báo lint. Effect này (như effect onIdTokenChanged bên dưới) CHỦ Ý
    // deps `[]`: chỉ đăng ký listener 1 lần lúc mount, không phải chạy lại mỗi khi state đổi.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Bug thật (đa-workspace, xem memory `project_multi_workspace_auth`): effect trên chỉ nghe
  // `onAuthStateChanged` — bắn lúc ĐĂNG NHẬP, không bắn khi token tự refresh cho phiên ĐANG MỞ. Nếu
  // admin đổi claim (cấp/đổi workspace) từ thiết bị khác trong lúc tab này vẫn mở, tab không có tín
  // hiệu nào để biết mà tự làm mới — cứ dùng profile cũ tới khi F5/đăng xuất-vào-lại, mọi hook đọc
  // `user.tenantId` (todos/unread-chats/permissions-context…) fire nhầm tenant → PERMISSION_DENIED.
  // `onIdTokenChanged` bắn CẢ lúc Firebase tự refresh token ngầm (~55'/lần với tab đang hoạt động) —
  // đủ để bắt claim mới trong vòng tối đa ~1h, không cần user tự thao tác gì.
  useEffect(() => {
    const auth = getFirebaseAuth();
    const unsub = onIdTokenChanged(auth, async (fbUser) => {
      if (!fbUser || fbUser.uid.startsWith('hk_')) return;
      if (switchingWorkspaceRef.current) return; // switchWorkspace() đang tự lo sync+áp state — đừng đua (xem khai báo ref)
      // Chỉ tái kiểm cho phiên ĐÃ đồng bộ xong lần đầu — lúc đăng nhập, `onAuthStateChanged` ở trên
      // lo hết rồi, `onIdTokenChanged` cũng bắn CÙNG lúc đó nhưng `syncedUidRef` chưa kịp set (còn
      // đang await syncUserProfile) nên tự bỏ qua, tránh đồng bộ trùng 2 lần khi vừa đăng nhập.
      if (syncedUidRef.current !== fbUser.uid) return;
      try {
        const tok = await fbUser.getIdTokenResult(false); // đọc local, không force-refresh
        if (tok.claims.impersonating) return; // đang impersonate — claim CỐ Ý khác, đừng đụng
        const claimTid = tok.claims.tenantId as string | undefined;
        const claimRole = tok.claims.gmsRole as string | undefined;
        if (claimTid === lastClaimRef.current?.tid && claimRole === lastClaimRef.current?.role) return; // không đổi gì
        lastClaimRef.current = { tid: claimTid, role: claimRole };
        const { profile, tenant, workspaces: ws } = await syncUserProfile(fbUser);
        if (profile) {
          setUser(profile);
          setBootTenant(tenant ?? null);
          setWorkspaces(ws && ws.length > 1 ? ws : null);
          saveBootProfile(fbUser.uid, profile, tenant ?? null);
          lastClaimRef.current = { tid: profile.tenantId, role: profile.role };
        }
      } catch (err) {
        logClientError({ route: 'lib/auth.tsx', action: 'reconcile_id_token_change', message: err instanceof Error ? err.message : String(err) });
      }
    });
    return unsub;
  }, []);

  // useCallback + useMemo: cả hai hàm này nằm trong `value` của Provider. Là arrow function trong
  // thân render thì chúng mới mỗi render ⇒ `value` mới ⇒ MỌI consumer re-render. Deps rỗng là đúng:
  // chúng chỉ dùng `setError` (setter useState, React bảo đảm ổn định) và import tĩnh.
  const signInWithGoogle = useCallback(async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(getFirebaseAuth(), provider);
    } catch (e: unknown) {
      const code = (e as { code?: string })?.code;
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        setError('Không thể đăng nhập bằng Google. Vui lòng thử lại.');
        throw e;
      }
      // ⚠️ 2 mã lỗi này VỐN coi là "user tự đóng popup" (im lặng hoàn toàn, không báo gì) — nhưng xác
      // nhận thật qua Console (2026-10-11, case Owner Portal dùng CHUNG hàm này): Chrome hiện đại áp
      // `Cross-Origin-Opener-Policy` lên `accounts.google.com` khiến Firebase SDK không gọi được
      // `window.closed` để biết popup còn mở hay đã đóng → nhầm lẫn y hệt "user tự đóng" dù popup có
      // thể vẫn đang chạy dở (không phải lỗi mạng/code GMS — phía Google, không sửa được). KHÔNG phân
      // biệt được 2 trường hợp ở tầng code (Firebase không lộ thêm thông tin) nên báo NHẸ thay vì im
      // lặng tuyệt đối — vô hại nếu đúng là user tự đóng (bỏ qua được), hữu ích nếu là COOP (biết bấm
      // lại thay vì tưởng app treo). Ảnh hưởng MỌI user (nhân viên lẫn chủ nhà), không riêng 1 luồng.
      setError('Cửa sổ đăng nhập Google vừa đóng mà chưa xong — nếu bạn chưa tự đóng, hãy bấm lại giúp.');
    }
  }, []);

  const signOut = useCallback(async () => {
    clearBootCache();
    await firebaseSignOut(getFirebaseAuth());
  }, []);

  // Đổi workspace đang làm việc (BR đối tác TA, xem app/api/auth/switch-workspace): mint claim mới
  // (server, PERSIST — khác impersonate ephemeral) rồi đăng nhập lại bằng custom token, áp claim mới
  // TRỰC TIẾP vào state (applySyncResult) + remount cây Provider qua key (xem AppShell.tsx) để MỌI
  // provider (tenant/building/permissions/todos…) đọc lại đúng tenant từ đầu — KHÔNG hard reload nữa.
  const switchWorkspace = useCallback(async (tenantId: string) => {
    const auth = getFirebaseAuth();
    const fbUser = auth.currentUser;
    if (!fbUser) return;
    const idToken = await fbUser.getIdToken();
    // Timeout: không có thì mạng chập chờn khiến fetch treo vô thời hạn, dropdown khoá cứng mãi
    // (Sidebar chỉ mở khoá lại trong catch — không có gì để bắt nếu request không bao giờ settle).
    const res = await fetch('/api/auth/switch-workspace', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken, tenantId }),
      signal: AbortSignal.timeout(SYNC_TIMEOUT_MS),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as { error?: string }).error || `switch-workspace failed: ${res.status}`);
    }
    const { token } = (await res.json()) as { token: string };
    // 🔴 Bật TRƯỚC signInWithCustomToken (xem khai báo `switchingWorkspaceRef`) — chặn 2 listener
    // onAuthStateChanged/onIdTokenChanged tự sync+áp state ĐUA với chính vòng retry-xác-nhận ngay dưới
    // đây (bug thật 2026-10-26, nanghome — xem comment khai báo ref để biết cơ chế đầy đủ).
    switchingWorkspaceRef.current = true;
    try {
      const cred = await signInWithCustomToken(auth, token);
      // Force-refresh + đọc thử `tenants/{id}` tới khi THẬT SỰ dùng được claim mới trước khi áp dụng —
      // xác nhận kênh realtime Firestore (TRONG CÙNG tab, không phải trang mới) đã swap credential xong.
      // 8 lần/400ms (3.2s) — nâng dần từ 5×300ms sau nhiều lần tái phát race permission-denied hàng loạt.
      await cred.user.getIdToken(true);
      for (let i = 0; i < 8; i++) {
        try { await getDoc(doc(getFirebaseDb(), 'tenants', tenantId)); break; }
        catch { await new Promise(r => setTimeout(r, 400)); }
      }
      // 🔴 BỎ HẲN `window.location.href` hard reload (2026-10-19) — đây mới là NGUỒN GỐC THẬT của race
      // "Missing or insufficient permissions" tái phát ~224 lần suốt từ 09-29 dù đã tăng ngân sách retry
      // ở TRÊN nhiều lần (5×300ms→8×400ms, vẫn không đủ 2026-10-01/10-18/10-19): hard reload buộc Firebase
      // Auth SDK của TRANG MỚI phục hồi phiên từ persistence IndexedDB — nhưng ghi IndexedDB của
      // `signInWithCustomToken()` vừa chạy Ở TRANG CŨ là ASYNC, có thể CHƯA FLUSH xong khi trang mới bắt
      // đầu đọc (xem comment ở nhánh `cacheConfirmed` trong `onAuthStateChanged` phía trên, từng vá y hệt
      // cho `impersonate()`) → trang MỚI đọc được claim CŨ dù vòng retry ngay phía trên đã xác nhận claim
      // MỚI hoạt động tốt. Mọi ngân sách retry trước/sau reload đều chỉ là phòng thủ XÁC SUẤT cho đúng 1
      // triệu chứng — gốc rễ là chính HÀNH ĐỘNG reload tự tạo ra 1 điểm race mới mỗi lần gọi.
      //
      // FIX TẬN GỐC: không bao giờ rời khỏi tab/phiên Auth hiện tại nữa — `cred.user` ở đây đã được xác
      // nhận claim mới hoạt động (vòng retry trên vừa đọc thành công), không cần đọc lại từ IndexedDB qua
      // 1 lượt tải trang nào cả. Áp claim mới TRỰC TIẾP vào state (applySyncResult, cùng hàm dùng cho mọi
      // luồng đăng nhập khác) rồi remount TOÀN BỘ cây Provider (Tenant/Building/Todos/Permissions…) qua
      // key `${uid}:${tenantId}` ở AppShell.tsx — đạt đúng hiệu quả "mọi provider đọc lại từ đầu" như hard
      // reload nhưng không có kênh IndexedDB nào phải race nữa.
      clearBootCache();
      const result = await syncUserProfile(cred.user);
      await applySyncResult(cred.user, result);
      router.push(landingPathFor(result.profile?.role, result.tenant));
    } finally {
      switchingWorkspaceRef.current = false;
    }
  }, [applySyncResult, router]);

  const value = useMemo(
    () => ({ user, ownerSession, firebaseUser, bootTenant, workspaces, switchWorkspace, loading, signInWithGoogle, signOut, error, setError, bootError, bootRetry, retryBoot }),
    [user, ownerSession, firebaseUser, bootTenant, workspaces, switchWorkspace, loading, signInWithGoogle, signOut, error, bootError, bootRetry, retryBoot],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
